// Readable, virtualised data table with a profile header per column:
// letter · name, type · distinct count and a valid / mismatch / missing bar.
// Nulls show as ∅, empty strings as "", type mismatches in red.
// `leadingRows` (sources only) are the file's rows above the data, shown
// first with the header row highlighted; `#` is then the file row number.
//
// Rows live in the data worker; the table asks for pages of PAGE_ROWS around
// what's on screen. While new pages load (a scroll, or a new run) it keeps
// showing what it had, so the table never flashes empty.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { dataHost } from "../data/dataHost";
import { displayValue } from "./display";
import { cellState } from "./profile";

export { displayValue };

const ROW_HEIGHT = 36;
const OVERSCAN = 8;
const PAGE_ROWS = 200;
const MAX_PAGES = 40; // pages kept in memory

// Column width from its header and a sample of its values.
const columnWidth = (column, index, rows) => {
  const sample = rows.slice(0, 200).map((row) => displayValue(row.values[index]).text.length);
  const longest = Math.max((column.label || column.name).length + 4, ...sample, 6);
  return Math.min(Math.max(longest * 8 + 32, 100), 360);
};

const QualityBar = ({ profile }) => {
  if (!profile?.total) return <div className="h-1 rounded-full bg-gray-200" />;
  const pct = (n) => `${(n / profile.total) * 100}%`;
  return (
    <div className="flex h-1 overflow-hidden rounded-full bg-gray-200" title={`${profile.ok} valid · ${profile.mismatch} mismatch · ${profile.null + profile.empty} missing`}>
      <div className="bg-teal-600" style={{ width: pct(profile.ok) }} />
      <div className="bg-red-500" style={{ width: pct(profile.mismatch) }} />
      <div className="bg-gray-300" style={{ width: pct(profile.null + profile.empty) }} />
    </div>
  );
};

// Pages of rows for one table, run and view. `key` changes whenever the rows
// would differ; the previous key's pages stay on screen until the new ones arrive.
// Old pages are only reused for the same table with the same columns.
const usePages = ({ scope, tableRef, columns, version, viewId, diffWith }) => {
  const key = JSON.stringify([tableRef, version, viewId ?? null, diffWith ?? null]);
  const sameTable = JSON.stringify([tableRef, columns.map((c) => c.name)]);
  const store = useRef({ key: null, sameTable: null, pages: new Map(), loading: new Set(), shown: new Map() });
  const [, setTick] = useState(0);
  const rerender = useCallback(() => setTick((n) => n + 1), []);

  if (store.current.key !== key) {
    // Keep showing the old pages (same table only); start a fresh cache for the new key.
    const previous = store.current;
    const shown = previous.sameTable !== sameTable ? new Map() : previous.pages.size ? previous.pages : previous.shown;
    store.current = { key, sameTable, pages: new Map(), loading: new Set(), shown };
  }

  const request = useCallback(
    (page) => {
      const current = store.current;
      if (current.pages.has(page) || current.loading.has(page)) return;
      current.loading.add(page);
      dataHost
        .getRows(scope, tableRef, { viewId, offset: page * PAGE_ROWS, limit: PAGE_ROWS, diffWith })
        .then((result) => {
          if (store.current !== current) return; // a newer key took over
          current.loading.delete(page);
          current.pages.set(page, result.rows);
          while (current.pages.size > MAX_PAGES) current.pages.delete(current.pages.keys().next().value);
          rerender();
        })
        .catch(() => current.loading.delete(page));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, scope, rerender],
  );

  const rowAt = (position) => {
    const page = Math.floor(position / PAGE_ROWS);
    const current = store.current;
    const rows = current.pages.get(page) ?? current.shown.get(page);
    return rows?.[position % PAGE_ROWS] ?? null;
  };

  return { key, request, rowAt, firstPage: store.current.pages.get(0) ?? store.current.shown.get(0) };
};

// `total` is the number of rows to show (of the view when filtering).
const DataTable = ({ scope = "editor", tableRef, version, columns, total, viewId, profile, highlightColumns, diffWith, jumpTo, leadingRows }) => {
  const scrollRef = useRef(null);
  const frame = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewHeight, setViewHeight] = useState(400);
  const [flash, setFlash] = useState(null);
  const { request, rowAt, firstPage } = usePages({ scope, tableRef, columns, version, viewId, diffWith });

  const profileByName = useMemo(() => Object.fromEntries((profile?.columns || []).map((c) => [c.name, c])), [profile]);

  // Widths come from the first page and only change when the columns do, so
  // a new run or a filter doesn't make columns jump.
  const columnsKey = JSON.stringify([tableRef, columns.map((c) => c.name)]);
  const [sized, setSized] = useState({ key: null, widths: [] });
  useEffect(() => {
    if (firstPage && sized.key !== columnsKey) setSized({ key: columnsKey, widths: columns.map((c, i) => columnWidth(c, i, firstPage)) });
  }, [firstPage, columnsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const widths = sized.key === columnsKey ? sized.widths : columns.map((c, i) => columnWidth(c, i, []));
  const totalWidth = 56 + widths.reduce((a, b) => a + b, 0);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(() => setViewHeight(element.clientHeight));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  // One state update per animation frame while scrolling.
  const onScroll = (event) => {
    const top = event.currentTarget.scrollTop;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => setScrollTop(top));
  };

  const leading = viewId ? [] : leadingRows || [];
  const leadingHeight = leading.length * ROW_HEIGHT;
  const bodyTop = scrollTop - leadingHeight;
  const first = Math.max(Math.floor(bodyTop / ROW_HEIGHT) - OVERSCAN, 0);
  const last = Math.max(Math.min(Math.ceil((bodyTop + viewHeight) / ROW_HEIGHT) + OVERSCAN, total), 0);

  // Load the pages on screen (and the first page, for column widths).
  useEffect(() => {
    request(0);
    for (let page = Math.floor(first / PAGE_ROWS); page <= Math.floor(Math.max(last - 1, 0) / PAGE_ROWS); page++) request(page);
  }, [first, last, request]);

  // Scroll to and briefly highlight a cell ("Jump to cell").
  useEffect(() => {
    if (!jumpTo || !scrollRef.current) return undefined;
    let cancelled = false;
    let timer;
    Promise.resolve(viewId ? dataHost.positionOf(viewId, jumpTo.row) : jumpTo.row).then((position) => {
      if (cancelled || position === -1 || !scrollRef.current) return;
      const columnIndex = columns.findIndex((c) => c.name === jumpTo.column);
      scrollRef.current.scrollTo({
        top: Math.max(leadingHeight + position * ROW_HEIGHT - viewHeight / 2, 0),
        left: Math.max(widths.slice(0, columnIndex).reduce((a, b) => a + b, 0) - 100, 0),
        behavior: "smooth",
      });
      setFlash(`${jumpTo.row}:${jumpTo.column}`);
      timer = setTimeout(() => setFlash(null), 1800);
    });
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [jumpTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const template = `56px ${widths.map((w) => `${w}px`).join(" ")}`;
  const positions = [];
  for (let position = first; position < last; position++) positions.push(position);

  return (
    <div ref={scrollRef} className="relative h-full overflow-auto" onScroll={onScroll}>
      <div style={{ width: totalWidth, minWidth: "100%" }}>
        <div className="sticky top-0 z-10 grid border-b border-gray-200 bg-gray-50" style={{ gridTemplateColumns: template }}>
          <div className="px-3 py-2 font-mono text-xs font-medium text-gray-500">#</div>
          {columns.map((column) => {
            const columnProfile = profileByName[column.name];
            const highlighted = highlightColumns?.has(column.name);
            return (
              <div key={column.name} className={`border-l border-gray-100 px-3 py-2 ${highlighted ? "bg-teal-50" : ""}`}>
                <div className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-900">
                  <span className="rounded bg-gray-800 px-1 font-mono text-[11px] font-medium leading-4 text-white">{columnProfile?.letter ?? column.letter}</span>
                  <span className="truncate" title={column.label || column.name}>
                    {column.label || column.name}
                  </span>
                </div>
                {columnProfile && (
                  <div className="mb-1.5 mt-0.5 font-mono text-[11px] font-medium text-gray-600">
                    {columnProfile.type} · {columnProfile.distinct.toLocaleString()} distinct
                  </div>
                )}
                <QualityBar profile={columnProfile} />
              </div>
            );
          })}
        </div>

        {leading.map((row) => (
          <div
            key={`l${row.number}`}
            className={`grid border-b ${row.header ? "border-emerald-300 bg-emerald-50 font-semibold text-emerald-900" : "border-gray-100 bg-gray-50 text-gray-400"}`}
            style={{ height: ROW_HEIGHT, gridTemplateColumns: template }}
            title={row.header ? "Header row: column names come from this row" : "Above the data: not passed to the steps"}
          >
            <div className="flex items-center gap-1 px-3 font-mono text-xs font-medium">
              {row.number}
              {row.header && <span className="rounded bg-emerald-600 px-1 text-[9px] uppercase leading-4 text-white">Header</span>}
            </div>
            {columns.map((column, columnIndex) => {
              const shown = displayValue(row.values[columnIndex]);
              return (
                <div key={column.name} className={`flex items-center overflow-hidden border-l border-gray-100 px-3 font-mono text-[13px] ${shown.numeric ? "justify-end" : ""}`}>
                  <span className="truncate">{shown.text}</span>
                </div>
              );
            })}
          </div>
        ))}

        <div style={{ height: total * ROW_HEIGHT, position: "relative" }}>
          {positions.map((position) => {
            const row = rowAt(position);
            const top = position * ROW_HEIGHT;
            if (!row) {
              return (
                <div key={`p${position}`} className="absolute left-0 grid border-b border-gray-100" style={{ top, height: ROW_HEIGHT, gridTemplateColumns: template, width: "100%" }}>
                  <div className="flex items-center px-3">
                    <span className="h-2.5 w-6 rounded bg-gray-100" />
                  </div>
                  {columns.map((column) => (
                    <div key={column.name} className="flex items-center border-l border-gray-100 px-3">
                      <span className="h-2.5 w-2/3 rounded bg-gray-100" />
                    </div>
                  ))}
                </div>
              );
            }
            const changed = row.changed ? new Set(row.changed) : null;
            return (
              <div
                key={position}
                className="absolute left-0 grid border-b border-gray-100 hover:bg-gray-50"
                style={{ top, height: ROW_HEIGHT, gridTemplateColumns: template, width: "100%" }}
              >
                <div className="flex items-center px-3 font-mono text-xs font-medium text-gray-500">{row.number ?? row.index + 1}</div>
                {columns.map((column, columnIndex) => {
                  const value = row.values[columnIndex];
                  const shown = displayValue(value);
                  const state = cellState(value, profileByName[column.name]?.type);
                  const flashing = flash === `${row.index}:${column.name}`;
                  return (
                    <div
                      key={column.name}
                      title={shown.text}
                      className={`flex items-center overflow-hidden border-l border-gray-100 px-3 font-mono text-[13px] ${
                        shown.numeric ? "justify-end" : ""
                      } ${state === "mismatch" ? "bg-red-50 text-red-700 ring-1 ring-inset ring-red-300" : ""} ${
                        changed?.has(columnIndex) ? "bg-teal-50" : ""
                      } ${flashing ? "!bg-amber-100 ring-2 ring-inset ring-amber-400" : ""} ${shown.muted ? "text-gray-500" : "text-gray-900"}`}
                    >
                      <span className="truncate">{shown.text}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        {!total && <div className="py-10 text-center text-sm text-gray-400">No rows to show.</div>}
      </div>
    </div>
  );
};

export default DataTable;
